import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useTooltip } from '@/hooks/useTooltip';
import { useAsyncData } from '@/hooks/useAsyncData';
import { search } from '@/api/search';

/** Столько же, сколько на сервере (SearchController::MIN_QUERY). */
const MIN_QUERY = 2;

/**
 * Поиск в шапке. Ищет по данным разделов, доступных пользователю: клиенты (по
 * названию, кабинету MT1, ОГРН и почте контакта), тесты каталога,
 * администраторы и роли — что именно, решает сервер по правам (см. докблок
 * SearchController в Laravel).
 *
 * Результат — сразу ссылка в нужное место, поэтому отдельной страницы
 * результатов нет: из шапки ищут, чтобы перейти, а не чтобы изучать выдачу.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, input, setInput] = useDebouncedSearch();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const tooltip = useTooltip<HTMLLabelElement>(
    'Поиск по клиентам, тестам, администраторам и ролям',
  );

  const enabled = query.length >= MIN_QUERY;
  const { data, error } = useAsyncData(
    enabled ? () => search(query) : null,
    [query],
    'Поиск сейчас недоступен.',
  );

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const reset = () => {
    setInput('');
    setOpen(false);
  };

  const go = (url: string) => {
    reset();
    navigate(url);
  };

  const groups = data?.groups ?? [];
  const showDropdown = open && enabled;

  return (
    <div className="global-search" ref={wrapRef}>
      {/* Что именно ищется — тултипом: в саму строку перечисление не влезает
          и обрезалось многоточием. */}
      <label className="topbar__search" ref={tooltip.ref} {...tooltip.handlers}>
        <Icon name="search" size={16} />
        <input
          type="text"
          value={input}
          placeholder="Поиск"
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') reset();
          }}
        />
        {/* Крестик — как в остальных полях поиска: сбрасывает строку и выдачу. */}
        {input && (
          <button
            type="button"
            className="topbar__search-clear"
            aria-label="Очистить поиск"
            onClick={reset}
          >
            <Icon name="close" size={12} />
          </button>
        )}
        {tooltip.node}
      </label>

      {showDropdown && (
        <div className="global-search__dropdown">
          {error && <p className="global-search__empty">{error}</p>}

          {!error && data === null && (
            <div className="global-search__loading">
              <Spinner />
            </div>
          )}

          {!error && data !== null && groups.length === 0 && (
            <p className="global-search__empty">Ничего не найдено.</p>
          )}

          {groups.map((group) => (
            <section className="global-search__group" key={group.key}>
              <header className="global-search__group-title">{group.title}</header>
              {group.items.map((item) => (
                <button
                  type="button"
                  className="global-search__item"
                  key={`${group.key}-${item.id}`}
                  onClick={() => go(item.url)}
                >
                  <span className="global-search__item-title">{item.title ?? 'Без названия'}</span>
                  {item.subtitle && (
                    <span className="global-search__item-sub">{item.subtitle}</span>
                  )}
                </button>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
